import { AttendeeType, MeetingAttendeeStatus } from '@alucio/aws-beacon-amplify/src/models';
import { isIOS, isMobile, isTablet } from 'react-device-detect';
import format from 'date-fns/format';
import { isArrayOfObjectsWithIds } from 'src/types/typeguards';
import { CRMAccount, CRMSubmitMeetingPayload } from '../../CRMIndexedDBTypes';
import {
  SUPPORTED_VEEVA_CONTROL_TYPES,
  VEEVA_VAULT_TABLES,
  VeevaVaultUpsertRecord,
} from '../../CRMVeevaVaultTypes';
import {
  getFieldApiName,
  getFieldDefinitionFromFormSettings,
  getObjectAPINameFromFieldKey,
  VeevaVaultBuildUpsertObjectOptions,
} from './VeevaVaultTranslatorUtils';
import { Singleton as IndexDbCrm } from '../../CRMIndexedDB';
import * as logger from 'src/utils/logger';
import type { ObjectWithId } from 'src/components/CustomFields/CustomFieldValueUtilities';

type TableValuesHandler = (opts: VeevaVaultBuildUpsertObjectOptions) => Promise<VeevaVaultUpsertRecord>;

export const VEEVA_VAULT_TABLE_VALUES_MAP: Partial<Record<string, TableValuesHandler>> = {
  [VEEVA_VAULT_TABLES.CALL]: getVeevaVaultCallAdditionalValues,
  [VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION]: getVeevaVaultMedicalDiscussionAdditionalValues,
  [VEEVA_VAULT_TABLES.MEDICAL_INQUIRY]: getMedicalInquiryAdditionalValues,
  [VEEVA_VAULT_TABLES.MEDICAL_INSIGHT]: getMedicalInsightAdditionalValues,
};

// INTERACTIONS //
async function getVeevaVaultCallAdditionalValues(
  options: VeevaVaultBuildUpsertObjectOptions): Promise<VeevaVaultUpsertRecord> {
  const { crmSubmitPayload, attendeesMapped } = options;

  if (!crmSubmitPayload) {
    throw Error('CRM Submit Payload is required to get the additional values for a Call record');
  }

  const callFormSetting = crmSubmitPayload.veevaVaultFormSettings
    .find((formSetting => formSetting.apiName === VEEVA_VAULT_TABLES.CALL));
  const mainAttendee = attendeesMapped?.primaryCRMAccount;
  const addressId = crmSubmitPayload.formValuesAttendees
    .find(({ attendeeType }) => attendeeType === AttendeeType.PRIMARY)
    ?.crmAddressId as string | undefined;

  if (!mainAttendee) {
    throw new Error('Main CRM Attendee not found on FormValueRecord/IndexDB');
  }

  const address = mainAttendee.addresses?.find((address) => address.id === addressId);
  const isPersonAccount = !!mainAttendee.ispersonaccount__v;
  const call_date__v = format(new Date(crmSubmitPayload.startTime), 'Y-MM-dd');
  const address__v = address ? `${address?.name__v}, ${address?.city_cda__v}, ` +
    `${address?.state_province__v} ${address?.postal_code_cda__v} ${address?.country__v}` : '';
  const device = getDevice();

  let numberOfAttendees = isPersonAccount ? 1 : 0;
  attendeesMapped?.additionalAttendees.forEach((attendee) => {
    if (attendee.account.ispersonaccount__v) {
      numberOfAttendees++;
    }
  });

  const numberOfUserAttendees = crmSubmitPayload.formValuesAttendees.filter((attendee) =>
    attendee.attendeeType === AttendeeType.CRM_USER &&
    attendee.crmUserId && attendee.status === MeetingAttendeeStatus.ACTIVE)?.length || 0;

  const response: {[key: string]: string | number} = {
    account__v: mainAttendee.id,
    address_line_1__v: address?.name__v || '',
    attendees__v: numberOfAttendees + numberOfUserAttendees,
    attendee_type__v : isPersonAccount ? 'person_account__v' : 'group_account__v',
    address__v,
    call2_status__v: crmSubmitPayload.status === 'PLANNED' ? 'planned__v' : 'saved__v',
    call_date__v,
    call_datetime__v: crmSubmitPayload.startTime,
    call_type__v : getCallType(crmSubmitPayload, mainAttendee),
    city__v: address?.city_cda__v || '',
    controlling_parent_account__v: mainAttendee.id,
    credentials__v: Array.isArray(mainAttendee.credentials__v)
      ? mainAttendee.credentials__v?.toString() || '' : mainAttendee.credentials__v || '',
    duration__v: 30,
    last_device__v: device,
    parent_address__v: address?.id || '',
    parent_address_id__v: address?.id || '',
    state_province__v: Array.isArray(address?.state_province__v)
      ? address?.state_province__v.toString() || '' : address?.state_province__v || '',
    zip__v: address?.postal_code_cda__v || '',
    zip_4__v: address?.zip_4__v || '',
  };

  if (isMobile) {
    response.mobile_last_modified_datetime__v = call_date__v;
  }

  const hasMedicalDiscussionsField = callFormSetting?.objectInfo.fields
    .find((field) => field.name === 'medical_discussions__v');

  if (hasMedicalDiscussionsField) {
    response.medical_discussions__v = getMedicalDiscussionsProductNames(crmSubmitPayload);
  }

  const businessAccountField = crmSubmitPayload.formValuesAttendees
    .find((attendee) => attendee.id === SUPPORTED_VEEVA_CONTROL_TYPES.BUSINESS_ACCOUNT);

  if (businessAccountField?.name) {
    response.location_text__v = businessAccountField.name;
  }

  return response;
}

// MEDICAL DISCUSSIONS //
async function getVeevaVaultMedicalDiscussionAdditionalValues(
  options: VeevaVaultBuildUpsertObjectOptions): Promise<VeevaVaultUpsertRecord> {
  const { parentObject } = options;
  return {
    attendee_type__v: parentObject?.attendee_type__v || '',
    account__v: parentObject?.account__v || '',
    call_date__v: parentObject?.call_date__v || '',
    user__v: parentObject?.user__v || '',
  };
}

// MEDICAL INSIGHTS //
export async function getMedicalInsightAdditionalValues (
  options: VeevaVaultBuildUpsertObjectOptions): Promise<VeevaVaultUpsertRecord> {
  const additionalFields: VeevaVaultUpsertRecord = {};
  const statusField = getFieldDefinitionFromFormSettings(
    [options.objectFormSetting],
    VEEVA_VAULT_TABLES.MEDICAL_INSIGHT,
    'medical_insight_status__v');

  if (options.isSubmit) {
    if (statusField?.editable) {
      additionalFields.medical_insight_status__v = 'submitted__v'
    } else {
      logger.veevaVaultSyncer.warn('Status field is not editable, skipping submission status...',
        { apiName: VEEVA_VAULT_TABLES.MEDICAL_INSIGHT });
    }
  }

  return additionalFields;
}

// MEDICAL INQUIRIES //
export async function getMedicalInquiryAdditionalValues(
  options: VeevaVaultBuildUpsertObjectOptions): Promise<VeevaVaultUpsertRecord> {
  const { crmValues } = options;
  const additionalFields: VeevaVaultUpsertRecord = {};
  const statusField = getFieldDefinitionFromFormSettings(
    [options.objectFormSetting],
    VEEVA_VAULT_TABLES.MEDICAL_INQUIRY,
    'medical_inquiry_status__v');

  const accountId = fieldValueToString(crmValues?.account__v);
  const addressId =  fieldValueToString(crmValues?.mail__v);

  if (options.isSubmit) {
    if (statusField?.editable) {
      additionalFields.medical_inquiry_status__v = 'submitted__v';
    } else {
      logger.veevaVaultSyncer.warn('Status field is not editable, skipping submission status...',
        { apiName: VEEVA_VAULT_TABLES.MEDICAL_INQUIRY });
    }
  }

  if (!accountId || !addressId) {
    return additionalFields;
  }

  const account = await IndexDbCrm.getById<CRMAccount>('ACCOUNT', accountId as string);

  if (!account) {
    return additionalFields;
  }

  const address = account.addresses?.find((addr) => addr.id === addressId);

  if (!address) {
    return additionalFields;
  }

  return {
    ...additionalFields,
    city__v: fieldValueToString(address.city_cda__v),
    country__v: fieldValueToString(address.country_value),
    state_province__v: fieldValueToString(address.state_province_value),
    zip__v: fieldValueToString(address.postal_code_cda__v),
    address_line_1__v: fieldValueToString(address.name__v),
  };
}

function fieldValueToString(value?: string | string[] | ObjectWithId[]): string {
  if (Array.isArray(value)) {
    return value.join('');
  }
  return value || '';
}

function getMedicalDiscussionsProductNames(crmSubmitPayload: CRMSubmitMeetingPayload): string {
  let medicalDiscussionProductNames: string = '';
  const productIds: string[] = [];

  // GETS THE IDS
  Object.keys(crmSubmitPayload.mainCrmValues).forEach((fieldKey) => {
    const isMedicalDiscussionField = getObjectAPINameFromFieldKey(fieldKey) === VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION;
    const medicalDiscussions = crmSubmitPayload.mainCrmValues[fieldKey];
    if (isMedicalDiscussionField && isArrayOfObjectsWithIds(medicalDiscussions)) {
      medicalDiscussions.forEach((medicalDiscussion) => {
        Object.keys(medicalDiscussion).forEach((fieldKey) => {
          if (getFieldApiName(fieldKey) === 'product__v' && medicalDiscussion[fieldKey]) {
            productIds.push(medicalDiscussion[fieldKey]?.toString() || '');
          }
        })
      });
    }
  })

  // GETS THE LABELS
  const medicalDiscussionFormSetting = crmSubmitPayload.veevaVaultFormSettings
    .find(({ apiName }) => apiName === VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION);

  const products = medicalDiscussionFormSetting?.lookups?.product__v;

  productIds.forEach((productId) => {
    const product = products?.find(({ id }) => id === productId);
    if (product) {
      medicalDiscussionProductNames += `${product.name__v}  `;
    }
  });

  return medicalDiscussionProductNames.trim();
}

function getDevice(): string {
  if (isTablet && isIOS) {
    return 'ipad__v';
  } else if (isMobile && isIOS) {
    return 'iphone__v';
  }
  return 'online__v';
}

function getCallType(_payload: CRMSubmitMeetingPayload, _account: CRMAccount) {
  // NOTE: The rest of the available options for a Call Type depend on other not yet implemented scenarios
  // https://vaultcrmhelp.veeva.com/doc/Content/CRM_topics/Call_Reporting_2/ConfiguringCR.htm
  return 'call_only__v';
}
